import React from 'react'
import { Icon, IconButton } from '@shopflo/ui'
import { MinusCircle, Plus, Trash } from '@phosphor-icons/react'
import { CatalogueDataType } from 'src/lib/types/common/catalogue'
import { ProductStatusTag } from './ProductStatusTag'
import { classNames } from 'src/lib/utils/helpers'
import { currencyFormatter } from 'src/lib/utils/formatters'
import { ProductIcon } from 'src/components/discounts/v2/common/ProductIcons'
import { useLocale } from 'src/lib/hooks/useLocal'

interface ExclusionListProps {
  exclusions: CatalogueDataType[]
  onRemoveExclusion: (index: number) => void
  onSwitchToExcludeMode: () => void
  catalogueType?: 'COLLECTION' | 'PRODUCT' | 'VARIANT' | 'PRODUCT_AND_VARIANT'
}

const ExclusionList: React.FC<ExclusionListProps> = ({
  exclusions,
  onRemoveExclusion,
  onSwitchToExcludeMode,
  catalogueType
}) => {
  const { t } = useLocale()
  const isCollection = catalogueType === 'COLLECTION'
  const excludePromptKey = isCollection
    ? 'product_search_dialog.exclude_optional_prompt_collections'
    : 'product_search_dialog.exclude_optional_prompt'
  const renderExclusionHeader = () => {
    if (exclusions.length > 0) {
      return (
        <div className='flex gap-3 pl-9 cursor-pointer' onClick={onSwitchToExcludeMode}>
          <div className='flex flex-col items-center '>
            <div className='h-2 w-[1px] bg-zinc-200' />
            <Icon IconComponent={MinusCircle} size='sm' className='text-zinc-400' />
            <div className='h-2 w-[1px] bg-zinc-200' />
          </div>
          <div className='flex flex-col justify-center'>
            <span className='text-text-xs-medium text-zinc-500 pl-2'>{t('product_search_dialog.excluding_items')}</span>
          </div>
        </div>
      )
    }

    return (
      <div className='flex items-center gap-3 cursor-pointer pl-7' onClick={onSwitchToExcludeMode}>
        <div className='relative p-2 flex items-center justify-center rounded-xl border-1 border-zinc-100 bg-white h-9 w-9'>
          <div className='absolute -top-4 left-1/2 h-4 w-[1px] -translate-x-1/2 bg-zinc-100' />
          <Icon IconComponent={MinusCircle} size='sm' className='text-zinc-400' />
        </div>
        <div className='flex flex-col'>
          <span className='text-text-sm-medium text-zinc-600'>
            {t(excludePromptKey)}{' '}
            <span className='text-text-xs-regular text-zinc-500'>{t('product_search_dialog.optional_label')}</span>
          </span>
        </div>
      </div>
    )
  }

  const renderExclusionList = () => {
    if (exclusions.length === 0) {
      return null
    }

    return (
      <div className='flex flex-col gap-1 pr-4 flex-1 overflow-y-auto'>
        {exclusions.map((item, index) => {
          const hasVariants = item.variants && item.variants.length > 0
          const price = !hasVariants && item.variants?.[0]?.price
          const inventory = !hasVariants && item.variants?.[0]?.inventory

          return (
            <div key={item.id} className='py-1'>
              <div className='grid grid-cols-[3fr_1fr_1fr] gap-4 items-center'>
                <div className='flex items-center gap-2'>
                  <IconButton
                    IconComponent={Trash}
                    size='xs'
                    variant='ghost'
                    className='text-zinc-500'
                    onClick={e => {
                      e.stopPropagation()
                      onRemoveExclusion(index)
                    }}
                  />
                  <div className='h-9 w-9 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center'>
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.description} className='w-full h-full object-cover' />
                    ) : (
                      <div>
                        <ProductIcon wrapperSize='md' />
                      </div>
                    )}
                  </div>
                  <span className='text-text-sm-medium text-zinc-800 line-clamp-1 pl-1'>{item.description}</span>
                  {item.status && <ProductStatusTag status={item.status} />}
                </div>
                <div className='text-right text-text-xs-regular text-zinc-600'>{inventory}</div>
                <div className='text-right text-text-xs-regular text-zinc-600'>{price && currencyFormatter(price)}</div>
              </div>
            </div>
          )
        })}

        <div className='flex items-center gap-2 py-1 pl-7 cursor-pointer' onClick={onSwitchToExcludeMode}>
          <div className='h-9 w-9 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center border-1 border-zinc-300 border-dashed'>
            <Icon IconComponent={Plus} size='sm' className='text-zinc-500' />
          </div>
          <p className='text-text-sm-medium text-zinc-600'>{t('product_search_dialog.add_more_exclusion')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className='h-full flex flex-col'>
      <div
        className={classNames(
          'grid grid-cols-[3fr_1fr_1fr] gap-4 items-center',
          exclusions.length > 0 ? 'py-0.5' : 'py-3'
        )}
      >
        {renderExclusionHeader()}
      </div>
      {renderExclusionList()}
    </div>
  )
}

export default ExclusionList
